import React, { useState, useEffect, useRef } from 'react';
import HeroSlider from '../components/HeroSlider';
import GoogleReviews from '../components/GoogleReviews';
import { packagesAPI, pagesAPI } from '../services/api';
import { ChevronLeft, ChevronRight, Plane, Bus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

// Card de Pacote estilo Concorrente
import { getOccupancyPercentage, getFutureDatesSorted, isDateUnavailable, isPackageClosed } from '../utils/occupancy';

const PackageCardHome = ({ pkg }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Verificar se TODAS as datas estão dentro de 90 dias (cartão com juros)
  const shouldApplyInterest = () => {
    if (!pkg.dates || pkg.dates.length === 0) return false;
    
    const now = new Date();
    
    for (const dateObj of pkg.dates) {
      if (dateObj.start_date) {
        try {
          const tripDate = new Date(dateObj.start_date);
          const diffDays = Math.floor((tripDate - now) / (1000 * 60 * 60 * 24));
          // Se existe alguma data com mais de 90 dias, não aplica juros
          if (diffDays >= 90) {
            return false;
          }
        } catch (e) {}
      }
    }
    
    // Se chegou aqui, TODAS as datas estão a menos de 90 dias
    return true;
  };

  // Se todas as datas do pacote estão a menos de 90 dias, cartão tem juros
  const hasCardInterest = shouldApplyInterest();

  const formatInstallment = (price) => {
    let finalPrice = price;
    
    // Se tem juros no cartão, aplicar taxa de ~2% ao mês (12 parcelas = 26.82% de juros)
    if (hasCardInterest) {
      const taxaMensal = 0.02; // 2% ao mês
      const numParcelas = 12;
      // Fórmula de juros compostos: PMT = PV * [i(1+i)^n] / [(1+i)^n - 1]
      const fator = Math.pow(1 + taxaMensal, numParcelas);
      const parcela = price * (taxaMensal * fator) / (fator - 1);
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
      }).format(parcela);
    }
    
    // Sem juros - divisão simples
    const installment = price / 12;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(installment);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'nacional-aereo': 'Nacional - Aéreo',
      'internacional-aereo': 'Internacional - Aéreo',
      'internacional': 'Internacional',
      'rodoviario': 'Rodoviário',
      'nacional-rodoviario': 'Rodoviário',
      'bate-volta': 'Bate e Volta',
      'passeio-1-dia': 'Passeio de 1 Dia'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'nacional-aereo': 'bg-blue-500',
      'internacional-aereo': 'bg-purple-500',
      'internacional': 'bg-purple-500',
      'rodoviario': 'bg-orange-500',
      'nacional-rodoviario': 'bg-orange-500',
      'bate-volta': 'bg-green-500',
      'passeio-1-dia': 'bg-green-500'
    };
    return colors[category] || 'bg-gray-500';
  };

  const getCategoryTextColor = (category) => {
    const colors = {
      'nacional-aereo': 'text-blue-500',
      'internacional-aereo': 'text-purple-500',
      'internacional': 'text-purple-500',
      'rodoviario': 'text-orange-500',
      'nacional-rodoviario': 'text-orange-500',
      'bate-volta': 'text-green-500',
      'passeio-1-dia': 'text-green-500'
    };
    return colors[category] || 'text-gray-500';
  };

  // Calcular ocupação da primeira data futura
  const futureDates = getFutureDatesSorted(pkg.dates);
  const totalSpots = futureDates.reduce((acc, d) => acc + (d.spots_available || 0), 0);
  const pkgClosed = isPackageClosed(pkg.dates);
  const firstDateOccupancy = futureDates.length > 0 
    ? getOccupancyPercentage(futureDates[0].max_spots, futureDates[0].spots_available, 0) 
    : { percentage: 0, isSoldOut: true };

  // Formatar datas (apenas futuras)
  const getDateDisplay = () => {
    if (futureDates.length === 0) return null;
    if (futureDates.length === 1) return futureDates[0].date;
    return `${futureDates.length} datas disponíveis`;
  };

  // Usar slug se disponível, senão usar ID
  const packageUrl = `/pacote/${pkg.slug || pkg.id}`;

  return (
    <div className="w-[160px] sm:w-[200px] md:w-[280px] bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
      {/* Imagem sem texto overlay */}
      <Link to={packageUrl} className="block relative">
        <div className="relative h-36 sm:h-44 md:h-52">
          <img
            src={pkg.main_image || pkg.image}
            alt={pkg.name}
            className={`w-full h-full object-cover ${pkgClosed ? 'grayscale' : ''}`}
            onError={(e) => { e.target.src = '/placeholder-package.jpg'; }}
          />
          {pkgClosed && (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <span data-testid={`home-pkg-closed-badge-${pkg.id}`} className="bg-red-600 text-white font-bold text-xs sm:text-sm px-3 py-1 rounded">ENCERRADO</span>
            </div>
          )}
          
          {/* Badge de Dias - Desktop (canto superior esquerdo) */}
          {pkg.duration_days && (
            <span className="hidden md:block absolute top-2 left-2 bg-white/90 text-gray-800 text-xs px-2 py-1 rounded font-semibold">
              {pkg.duration_days} dias
            </span>
          )}
          
          {/* Badge de Categoria */}
          <span className={`absolute top-2 right-2 ${getCategoryColor(pkg.category)} text-white text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded font-semibold`}>
            {getCategoryLabel(pkg.category)}
          </span>
        </div>
      </Link>

      {/* Informações abaixo da imagem */}
      <div className="p-2 sm:p-3">
        {/* Nome do pacote - MAIOR que o preço */}
        <Link to={packageUrl}>
          <h3 className="text-base sm:text-lg md:text-xl font-extrabold text-gray-900 leading-tight line-clamp-2 hover:text-theme-primary transition-colors mb-1" style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif" }}>
            {pkg.name}
          </h3>
        </Link>

        {/* Preço - SEGUNDO */}
        <div className="mb-0.5">
          <div className="flex flex-wrap items-center gap-1">
            {pkg.discount_percentage > 0 && pkg.original_price ? (
              <>
                <p className="text-[10px] sm:text-xs text-gray-400 line-through">
                  {formatPrice(pkg.original_price)}
                </p>
                <p className="text-sm sm:text-base md:text-lg font-bold text-green-600">
                  {formatPrice(pkg.price)}
                </p>
              </>
            ) : (
              <p className="text-sm sm:text-base md:text-lg font-bold text-theme-primary">
                {formatPrice(pkg.price)}
              </p>
            )}
            {/* Dias - Mobile (ao lado do preço) */}
            {pkg.duration_days && (
              <span className={`md:hidden text-[9px] sm:text-[10px] font-semibold ${getCategoryTextColor(pkg.category)}`}>
                ({pkg.duration_days} dias)
              </span>
            )}
          </div>
          {pkg.discount_percentage > 0 && (
            <span className="text-[8px] sm:text-[10px] bg-green-100 text-green-700 px-1 py-0.5 rounded font-bold">
              -{pkg.discount_percentage}% OFF
            </span>
          )}
        </div>

        {/* Datas - TERCEIRO - Cada data com seu status individual */}
        {futureDates.length > 0 && (
          <div className="mb-1 space-y-0.5" data-testid={`pkg-dates-${pkg.id}`}>
            {futureDates.slice(0, 3).map((d, idx) => {
              const soldOut = isDateUnavailable(d);
              return (
                <div key={idx} className="flex items-center justify-between text-[9px] sm:text-[10px] md:text-xs">
                  <span className="text-gray-600 truncate">{d.date}</span>
                  <span className={`px-1 rounded font-bold ml-1 ${soldOut ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {soldOut ? 'ENCERRADO' : 'DISPONÍVEL'}
                  </span>
                </div>
              );
            })}
            {futureDates.length > 3 && (
              <p className="text-[9px] text-gray-400 italic">+{futureDates.length - 3} outras datas</p>
            )}
          </div>
        )}

        {/* Parcelamento - QUARTO */}
        <p className="text-[8px] sm:text-[9px] md:text-xs text-gray-500 truncate mb-1">
          até 12x {formatInstallment(pkg.price)} no cartão
        </p>

        {/* Botão Comprar - COR AMARELA */}
        <Link
          to={packageUrl}
          className="block w-full btn-primary-theme text-center font-bold py-1.5 sm:py-2 rounded-lg transition-colors text-[10px] sm:text-xs md:text-sm"
        >
          {pkgClosed ? 'Ver Detalhes' : 'Comprar'}
        </Link>
      </div>
    </div>
  );
};

// Seção de Carrossel
const PackageSection = ({ title, subtitle, packages }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      return () => ref.removeEventListener('scroll', checkScroll);
    }
  }, [packages]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!packages || packages.length === 0) return null;

  return (
    <div className="mb-8 sm:mb-12">
      {/* Título e Subtítulo */}
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 italic">
          {title}
        </h2>
        {subtitle && (
          <p className="text-sm sm:text-base text-gray-500 mt-1">
            {subtitle}
          </p>
        )}
      </div>

      {/* Carrossel */}
      <div className="relative px-2 sm:px-4">
        {/* Botões de navegação - Desktop */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 bg-white shadow-xl rounded-full p-3 hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </button>
        )}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 bg-white shadow-xl rounded-full p-3 hover:bg-gray-100 transition-colors"
          >
            <ChevronRight className="w-6 h-6 text-gray-700" />
          </button>
        )}

        {/* Lista de Cards - UMA LINHA com scroll horizontal em todas versões */}
        <div
          ref={scrollRef}
          className="flex gap-3 md:gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide px-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {packages.map((pkg) => (
            <div key={pkg.id} className="snap-start flex-shrink-0 w-[160px] sm:w-[200px] md:w-[280px]">
              <PackageCardHome pkg={pkg} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageContent, setPageContent] = useState({});
  const [campaignMode, setCampaignMode] = useState(null);
  const [cardIndicator, setCardIndicator] = useState('percentage');
  const { themeConfig } = useTheme();
  const primary = themeConfig?.theme?.primary || '#003366';
  const secondary = themeConfig?.theme?.secondary || '#FFD700';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [packagesData, pageData, siteSettings] = await Promise.all([
        packagesAPI.getAll(),
        pagesAPI.getByName('inicio').catch(() => null),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/packages/site-settings`).then(r => r.json()).catch(() => ({}))
      ]);
      setPackages(packagesData);
      if (pageData && pageData.content) {
        setPageContent(pageData.content);
      }
      // Verificar modo campanha e indicador
      if (siteSettings?.campaign_active) {
        setCampaignMode(siteSettings);
      }
      if (siteSettings?.card_indicator) {
        setCardIndicator(siteSettings.card_indicator);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para obter a próxima data futura de um pacote
  const getNextFutureDate = (pkg) => {
    if (!pkg.dates || pkg.dates.length === 0) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Filtrar apenas datas futuras e ordenar
    const futureDates = pkg.dates
      .filter(d => {
        if (!d.start_date) return false;
        const dateStr = d.start_date.split('T')[0];
        const date = new Date(dateStr + 'T00:00:00');
        return date >= today;
      })
      .sort((a, b) => {
        const dateA = new Date(a.start_date.split('T')[0] + 'T00:00:00');
        const dateB = new Date(b.start_date.split('T')[0] + 'T00:00:00');
        return dateA - dateB;
      });
    
    return futureDates.length > 0 ? futureDates[0] : null;
  };

  // Ordenar pacotes por próxima data futura
  const sortByNextFutureDate = (pkgList) => {
    return [...pkgList].sort((a, b) => {
      const nextDateA = getNextFutureDate(a);
      const nextDateB = getNextFutureDate(b);
      
      // Pacotes sem data futura vão para o final
      if (!nextDateA && !nextDateB) return 0;
      if (!nextDateA) return 1;
      if (!nextDateB) return -1;
      
      const dateA = new Date(nextDateA.start_date.split('T')[0] + 'T00:00:00');
      const dateB = new Date(nextDateB.start_date.split('T')[0] + 'T00:00:00');
      
      return dateA - dateB;
    });
  };

  // Filtrar pacotes que ainda têm pelo menos uma data futura
  const filterPackagesWithFutureDates = (pkgList) => {
    return pkgList.filter(pkg => getNextFutureDate(pkg) !== null);
  };

  // Filtrar pacotes por categoria, remover os sem datas futuras E ordenar por próxima data futura
  const aereoPackages = sortByNextFutureDate(
    filterPackagesWithFutureDates(
      packages.filter(p => 
        p.category === 'nacional-aereo' || p.category === 'internacional' || p.category === 'internacional-aereo'
      )
    )
  );
  const rodoviarioPackages = sortByNextFutureDate(
    filterPackagesWithFutureDates(
      packages.filter(p => 
        p.category === 'nacional-rodoviario' || p.category === 'bate-volta' || p.category === 'rodoviario' || p.category === 'passeio-1-dia'
      )
    )
  );

  return (
    <div 
      className="min-h-screen" 
      style={{ backgroundColor: themeConfig?.theme?.backgroundLight || '#f3f4f6' }}
    >
      {/* Hero: Campanha (fullscreen) ou Slider */}
      {campaignMode ? (
        <div 
          className="relative w-full h-screen overflow-hidden flex items-center justify-center"
          style={{
            backgroundImage: `url(${campaignMode.campaign_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
            {campaignMode.campaign_text && (
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight drop-shadow-xl">
                {campaignMode.campaign_text}
              </h1>
            )}
            {campaignMode.campaign_link && (
              <Link
                to={campaignMode.campaign_link}
                className="inline-block mt-8 font-bold px-10 py-5 rounded-xl text-lg transition-all hover:scale-105 shadow-xl"
                style={{ backgroundColor: secondary, color: '#1f2937' }}
              >
                Saiba Mais
              </Link>
            )}
          </div>
        </div>
      ) : (
        <HeroSlider pageContent={pageContent} />
      )}

      {/* Pacotes */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
            </div>
          ) : (
            <>
              {/* Pacotes Aéreos */}
              <PackageSection
                title="Pacotes Aéreos"
                subtitle="(Saídas de RJ e SP)"
                packages={aereoPackages}
              />

              {/* Pacotes Rodoviários */}
              <PackageSection
                title="Rodoviário e Passeios"
                subtitle="(Saídas da Rodoviária Novo Rio)"
                packages={rodoviarioPackages}
              />
            </>
          )}

          {/* Botão Ver Todos */}
          <div className="text-center mt-8 px-4">
            <Link
              to="/pacotes"
              className="inline-block bg-theme-primary text-white font-bold px-8 py-4 rounded-lg transition-all duration-300 hover:scale-105 text-base sm:text-lg"
            >
              Ver Todos os Pacotes
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews do Google */}
      <GoogleReviews />
    </div>
  );
};

export default Home;
